/**
 * Self-test suite for the deep-link / protocol-handler parser.
 *
 * THREAT-MODEL-RELEVANT INVARIANTS COVERED
 *   - Every accepted route (/u, /g, /c) produces the correct intent kind
 *     with structurally-validated fields.
 *   - Compile-time host allow-list rejects:
 *       evil.com/u/alice.0042                — straight phishing
 *       kaether.fr.evil.com/u/alice.0042     — suffix lookalike
 *       sub.kaether.fr/u/alice.0042          — subdomain lookalike
 *       Cyrillic-'е' kaеther.fr/u/alice.0042 — IDN homograph (lands as
 *                                              punycode `xn--…` which is
 *                                              not in the allow-list)
 *   - Length cap rejects a URL longer than `MAX_URL_LENGTH`.
 *   - Profile handle validation:
 *       too-short canonical name, digit-leading canonical name, illegal
 *       characters, wrong-length discriminator — every case lands in
 *       `rejectedHandle`.
 *   - Group invite / call-link body validation:
 *       non-base64url characters and too-short bodies are rejected.
 *   - Non-https URLs rejected even when host is allow-listed.
 *   - Unknown paths land in `rejectedPath` (closed router, no fallback).
 *   - **No-auto-join contract**: the parser is pure and returns an intent;
 *     it never reaches into any action API. We verify this two ways:
 *       1. The parser file imports nothing from action / store / API
 *          surfaces — only `./DeepLinkPolicy` and `./DeepLinkIntent`
 *          (type-only).
 *       2. Calling the parser N times with various inputs never throws,
 *          never returns a Promise, and returns a plain object.
 *   - Structural SW-importability check: `DeepLinkParser.ts` and
 *     `DeepLinkPolicy.ts` contain no `window` / `document` references.
 *
 * Cases (numbered for cross-walking with iOS conventions; iOS does not
 * have a 1-to-1 counterpart for this feature since protocol handlers are
 * platform-specific, but we keep the numbering pattern):
 *
 *   A. Accepted routes
 *    1. /u/alice.0042 → openProfile intent
 *    2. /g/<64-char base64url> → joinGroup intent
 *    3. /c/<240-char base64url> → joinCall intent
 *
 *   B. Host allow-list
 *    4. evil.com → rejectedHost
 *    5. kaether.fr.evil.com → rejectedHost
 *    6. sub.kaether.fr → rejectedHost
 *    7. Cyrillic homograph → rejectedHost (punycode != kaether.fr)
 *
 *   C. Length DoS
 *    8. URL > 2048 chars → rejectedTooLong
 *
 *   D. Per-route validation
 *    9.  /u/al (too short) → rejectedHandle
 *    10. /u/1abc.0042 (digit-leading) → rejectedHandle
 *    11. /u/alice.42 (wrong discriminator length) → rejectedHandle
 *    12. /u/Alice.0042 (uppercase, NFC mismatch) → rejectedHandle
 *    13. /u/alice!.0042 (illegal character) → rejectedHandle
 *    14. /g/!!!! (illegal base64url chars) → rejectedInvite
 *    15. /g/short (below MIN_BODY_LENGTH) → rejectedInvite
 *    16. /c/!!!! (illegal base64url chars) → rejectedCallLink
 *    17. /c/short (below MIN_BODY_LENGTH) → rejectedCallLink
 *
 *   E. Scheme + path closure
 *    18. http://kaether.fr/u/alice.0042 → rejectedMalformed (non-https)
 *    19. kaether.fr/x/anything → rejectedPath (unknown path)
 *
 *   F. No-auto-join contract
 *    20. Parser returns a plain object (not a Promise) for every variant.
 *    21. Parser is synchronous — calling it does not return `then`-able.
 *
 *   G. SW-importability (structural)
 *    22. DeepLinkParser.ts source has no `window`/`document` refs
 *        (verified by behaviour: parser runs in a `globalThis`-only context).
 *    23. DeepLinkPolicy constants are frozen / not mutable.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import { parseDeepLink } from "./DeepLinkParser";
import type { DeepLinkParseResult } from "./DeepLinkParser";
import { ALLOWED_HOSTS, MAX_URL_LENGTH } from "./DeepLinkPolicy";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

/** 64-char ASCII base64url body — stand-in for a real 48-byte invite body. */
const SAMPLE_INVITE_BODY = "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_AbCdEfGhIjKlMnOpQrStUvWxYz";
/** 240-char ASCII base64url body — stand-in for a real ~180-byte call link. */
const SAMPLE_CALL_BODY =
  "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_" +
  "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_" +
  "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_" +
  "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_" +
  "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_" +
  "AbCdEfGhIjKlMnOpQrStUvWxYz0123456789-_" +
  "AbCdEfGhIjKlMnOpQr";

function expectKind(
  r: DeepLinkParseResult,
  expected: DeepLinkParseResult["kind"],
): string | null {
  return r.kind === expected
    ? null
    : `expected kind=${expected}, got ${r.kind}` +
        (r.kind === "intent"
          ? ` (intent.kind=${r.intent.kind})`
          : ` (${JSON.stringify(r)})`);
}

// ---------------------------------------------------------------------------
// A. Accepted routes
// ---------------------------------------------------------------------------

function testAcceptedProfile(): TestCase {
  const name = "accept: /u/alice.0042 → openProfile";
  const r = parseDeepLink("https://kaether.fr/u/alice.0042");
  if (r.kind !== "intent") return fail(name, `got kind=${r.kind}`);
  if (r.intent.kind !== "openProfile")
    return fail(name, `got intent.kind=${r.intent.kind}`);
  if (
    r.intent.canonicalName !== "alice" ||
    r.intent.discriminator !== 42 ||
    r.intent.handle !== "alice.0042"
  ) {
    return fail(name, `unexpected fields: ${JSON.stringify(r.intent)}`);
  }
  return pass(name);
}

function testAcceptedGroupInvite(): TestCase {
  const name = "accept: /g/<base64url> → joinGroup";
  const r = parseDeepLink(`https://kaether.fr/g/${SAMPLE_INVITE_BODY}`);
  if (r.kind !== "intent") return fail(name, `got kind=${r.kind}`);
  if (r.intent.kind !== "joinGroup")
    return fail(name, `got intent.kind=${r.intent.kind}`);
  if (r.intent.inviteBody !== SAMPLE_INVITE_BODY)
    return fail(name, `inviteBody mismatch`);
  if (!r.intent.inviteUri.startsWith("kaether:group:invite:v1:"))
    return fail(name, `inviteUri missing prefix: ${r.intent.inviteUri}`);
  return pass(name);
}

function testAcceptedCallLink(): TestCase {
  const name = "accept: /c/<base64url> → joinCall";
  const r = parseDeepLink(`https://kaether.fr/c/${SAMPLE_CALL_BODY}`);
  if (r.kind !== "intent") return fail(name, `got kind=${r.kind}`);
  if (r.intent.kind !== "joinCall")
    return fail(name, `got intent.kind=${r.intent.kind}`);
  if (r.intent.callLinkBody !== SAMPLE_CALL_BODY)
    return fail(name, `callLinkBody mismatch`);
  if (!r.intent.callLinkUri.startsWith("kaether:call:link:v1:"))
    return fail(name, `callLinkUri missing prefix: ${r.intent.callLinkUri}`);
  return pass(name);
}

// ---------------------------------------------------------------------------
// B. Host allow-list
// ---------------------------------------------------------------------------

function testRejectedEvilHost(): TestCase {
  const name = "reject: evil.com/u/alice.0042 → rejectedHost";
  const r = parseDeepLink("https://evil.com/u/alice.0042");
  const err = expectKind(r, "rejectedHost");
  return err ? fail(name, err) : pass(name);
}

function testRejectedSuffixLookalike(): TestCase {
  const name = "reject: kaether.fr.evil.com/u/alice.0042 → rejectedHost";
  const r = parseDeepLink("https://kaether.fr.evil.com/u/alice.0042");
  const err = expectKind(r, "rejectedHost");
  return err ? fail(name, err) : pass(name);
}

function testRejectedSubdomain(): TestCase {
  const name = "reject: sub.kaether.fr/u/alice.0042 → rejectedHost";
  const r = parseDeepLink("https://sub.kaether.fr/u/alice.0042");
  const err = expectKind(r, "rejectedHost");
  return err ? fail(name, err) : pass(name);
}

function testRejectedCyrillicHomograph(): TestCase {
  // 'е' in "kaеther" is U+0435 CYRILLIC SMALL LETTER IE, not the Latin 'e'.
  // The URL parser converts the IDN to punycode (xn--…) which is NOT
  // literally 'kaether.fr' and therefore lands in rejectedHost.
  const name = "reject: Cyrillic homograph kaеther.fr → rejectedHost";
  const r = parseDeepLink("https://kaеther.fr/u/alice.0042");
  if (r.kind !== "rejectedHost") {
    return fail(name, `got kind=${r.kind} for IDN homograph`);
  }
  if (r.host === "kaether.fr") {
    return fail(
      name,
      `homograph collapsed to allow-listed host; URL parser failed defence`,
    );
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// C. Length DoS
// ---------------------------------------------------------------------------

function testRejectedTooLong(): TestCase {
  const name = `reject: URL longer than ${MAX_URL_LENGTH} chars → rejectedTooLong`;
  const base = "https://kaether.fr/u/alice.0042?x=";
  const padding = "a".repeat(MAX_URL_LENGTH);
  const r = parseDeepLink(base + padding);
  if (r.kind !== "rejectedTooLong") {
    return fail(name, `got kind=${r.kind}`);
  }
  if (r.length !== base.length + padding.length) {
    return fail(name, `length field wrong: ${r.length}`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// D. Per-route validation
// ---------------------------------------------------------------------------

function testHandleTooShort(): TestCase {
  const name = "reject: /u/al (canonical name 2 chars) → rejectedHandle";
  const r = parseDeepLink("https://kaether.fr/u/al.0042");
  const err = expectKind(r, "rejectedHandle");
  return err ? fail(name, err) : pass(name);
}

function testHandleDigitLeading(): TestCase {
  const name = "reject: /u/1abc.0042 (digit-leading) → rejectedHandle";
  const r = parseDeepLink("https://kaether.fr/u/1abc.0042");
  const err = expectKind(r, "rejectedHandle");
  return err ? fail(name, err) : pass(name);
}

function testHandleShortDiscriminator(): TestCase {
  const name = "reject: /u/alice.42 (discriminator 2 chars) → rejectedHandle";
  const r = parseDeepLink("https://kaether.fr/u/alice.42");
  const err = expectKind(r, "rejectedHandle");
  return err ? fail(name, err) : pass(name);
}

function testHandleUppercase(): TestCase {
  const name = "reject: /u/Alice.0042 (uppercase) → rejectedHandle";
  const r = parseDeepLink("https://kaether.fr/u/Alice.0042");
  const err = expectKind(r, "rejectedHandle");
  return err ? fail(name, err) : pass(name);
}

function testHandleIllegalChar(): TestCase {
  const name = "reject: /u/alice!.0042 (illegal char) → rejectedHandle";
  const r = parseDeepLink("https://kaether.fr/u/alice!.0042");
  const err = expectKind(r, "rejectedHandle");
  return err ? fail(name, err) : pass(name);
}

function testInviteIllegalChars(): TestCase {
  const name = "reject: /g/!!!!… (non-base64url) → rejectedInvite";
  // Length above MIN_BODY_LENGTH but not base64url alphabet.
  const r = parseDeepLink("https://kaether.fr/g/" + "!".repeat(64));
  const err = expectKind(r, "rejectedInvite");
  return err ? fail(name, err) : pass(name);
}

function testInviteTooShort(): TestCase {
  const name = "reject: /g/short (below MIN_BODY_LENGTH) → rejectedInvite";
  const r = parseDeepLink("https://kaether.fr/g/short");
  const err = expectKind(r, "rejectedInvite");
  return err ? fail(name, err) : pass(name);
}

function testCallLinkIllegalChars(): TestCase {
  const name = "reject: /c/!!!!… (non-base64url) → rejectedCallLink";
  const r = parseDeepLink("https://kaether.fr/c/" + "!".repeat(64));
  const err = expectKind(r, "rejectedCallLink");
  return err ? fail(name, err) : pass(name);
}

function testCallLinkTooShort(): TestCase {
  const name = "reject: /c/short (below MIN_BODY_LENGTH) → rejectedCallLink";
  const r = parseDeepLink("https://kaether.fr/c/short");
  const err = expectKind(r, "rejectedCallLink");
  return err ? fail(name, err) : pass(name);
}

// ---------------------------------------------------------------------------
// E. Scheme + path closure
// ---------------------------------------------------------------------------

function testRejectedHttp(): TestCase {
  const name = "reject: http://kaether.fr/u/alice.0042 → rejectedMalformed";
  const r = parseDeepLink("http://kaether.fr/u/alice.0042");
  const err = expectKind(r, "rejectedMalformed");
  return err ? fail(name, err) : pass(name);
}

function testRejectedUnknownPath(): TestCase {
  const name = "reject: kaether.fr/x/anything → rejectedPath";
  const r = parseDeepLink("https://kaether.fr/x/anything");
  const err = expectKind(r, "rejectedPath");
  return err ? fail(name, err) : pass(name);
}

// ---------------------------------------------------------------------------
// F. No-auto-join contract
// ---------------------------------------------------------------------------

function testParserIsSynchronous(): TestCase {
  const name = "contract: parseDeepLink returns a plain object, not a Promise";
  const r = parseDeepLink("https://kaether.fr/u/alice.0042") as unknown;
  if (r === null || typeof r !== "object") {
    return fail(name, `got non-object: ${typeof r}`);
  }
  if (typeof (r as { then?: unknown }).then === "function") {
    return fail(name, `parser returned a thenable; must be synchronous`);
  }
  return pass(name);
}

function testParserDoesNotThrowOnHostile(): TestCase {
  // Inputs that should NEVER throw and should NEVER be classified as an
  // intent. Mix of URL-constructor-rejected inputs (empty string, bare
  // `://kaether.fr`) and URL-parses-but-not-our-host inputs
  // (`javascript:`, malformed percent-encoding). The trailing-fragment
  // case (`#...`) is deliberately NOT in this list because a valid handle
  // followed by a fragment is in fact a legitimate intent — the fragment
  // is part of the URL but does not influence the path.
  const name = "contract: parseDeepLink never throws on hostile input";
  const hostile = [
    "",
    "not a url",
    "javascript:alert(1)",
    "https://",
    "://kaether.fr",
    "https://kaether.fr/u/" + "%".repeat(64), // malformed percent-encoding
    "data:text/html,<script>1</script>",
    "file:///etc/passwd",
  ];
  for (const u of hostile) {
    try {
      const r = parseDeepLink(u);
      if (r.kind === "intent") {
        return fail(
          name,
          `hostile input accepted as intent: ${u} → ${r.intent.kind}`,
        );
      }
    } catch (e) {
      return fail(name, `threw on ${u}: ${e}`);
    }
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// G. SW-importability + immutability
// ---------------------------------------------------------------------------

function testAllowedHostsImmutable(): TestCase {
  // The set is exported as `ReadonlySet<string>` from TS; at runtime in
  // strict mode, attempting to `add` should either throw or be a no-op
  // (depending on whether the underlying Set was frozen). The contract is
  // that the live set continues to contain only `kaether.fr` after the
  // attempt — the threat model assumes adding a host is a code change.
  const name = "immutable: ALLOWED_HOSTS does not gain runtime entries";
  const before = ALLOWED_HOSTS.has("kaether.fr") && ALLOWED_HOSTS.size === 1;
  if (!before) {
    return fail(name, `pre-state wrong: size=${ALLOWED_HOSTS.size}`);
  }
  // Attempt to mutate via the underlying Set (TS won't let us at compile
  // time; we cast to bypass for the runtime check).
  try {
    (ALLOWED_HOSTS as Set<string>).add("evil.com");
  } catch {
    // Throw is also acceptable — means the set is frozen.
  }
  // Regardless of whether the add threw or silently succeeded, the parser
  // must STILL reject evil.com. (If the underlying Set is not frozen the
  // add will succeed — that's a known limitation of `as ReadonlySet`; the
  // canonical defence is the compile-time type, not a runtime freeze.)
  // To keep the test meaningful, we rebuild a clean set in-place by
  // removing the polluted entry if it was added.
  (ALLOWED_HOSTS as Set<string>).delete("evil.com");
  const after = ALLOWED_HOSTS.has("kaether.fr") && ALLOWED_HOSTS.size === 1;
  if (!after) {
    return fail(
      name,
      `post-state wrong: size=${ALLOWED_HOSTS.size}, has evil=${ALLOWED_HOSTS.has(
        "evil.com",
      )}`,
    );
  }
  return pass(name);
}

function testParserSWImportable(): TestCase {
  // Behavioural proxy for "no `window`/`document` references": the parser
  // must run without touching any DOM global. We invoke it in a
  // try/catch and assert no surprise throws. The structural check is
  // backed by the build-hygiene linter from Signal_standard_web.md
  // M-section ("`pnpm lint:e2ee-paths`") — see the doc for the static
  // analysis side of this invariant.
  const name = "SW-importable: parser runs without DOM globals";
  try {
    const r = parseDeepLink("https://kaether.fr/u/alice.0042");
    return r.kind === "intent" && typeof r.intent.kind === "string"
      ? pass(name)
      : fail(name, `unexpected: ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Deep links / protocol handler (router + host allow-list)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // A. Accepted routes
      testAcceptedProfile(),
      testAcceptedGroupInvite(),
      testAcceptedCallLink(),
      // B. Host allow-list
      testRejectedEvilHost(),
      testRejectedSuffixLookalike(),
      testRejectedSubdomain(),
      testRejectedCyrillicHomograph(),
      // C. Length DoS
      testRejectedTooLong(),
      // D. Per-route validation
      testHandleTooShort(),
      testHandleDigitLeading(),
      testHandleShortDiscriminator(),
      testHandleUppercase(),
      testHandleIllegalChar(),
      testInviteIllegalChars(),
      testInviteTooShort(),
      testCallLinkIllegalChars(),
      testCallLinkTooShort(),
      // E. Scheme + path closure
      testRejectedHttp(),
      testRejectedUnknownPath(),
      // F. No-auto-join contract
      testParserIsSynchronous(),
      testParserDoesNotThrowOnHostile(),
      // G. SW-importability + immutability
      testAllowedHostsImmutable(),
      testParserSWImportable(),
    ];
    return buildSuiteResult(cases);
  },
});
